import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useWriteContract, useWaitForTransactionReceipt } from 'wagmi'
import { type Hash } from 'viem'
import {
  VOUCH_CONTRACT_ADDRESS,
  VOUCH_ABI,
  fetchCommitment,
  fetchIsInChallengeWindow,
  formatMon,
  shortAddr,
  statusLabel,
  vTypeLabel,
} from '../lib/contract'
import { fetchSpec, fetchEvidence, type StoredSpec, type StoredEvidence } from '../lib/api'

interface Props {
  address: `0x${string}` | undefined
  isConnected: boolean
}

const ZERO_BYTES32 = '0x' + '0'.repeat(64) as `0x${string}`

function StatusBadge({ status }: { status: number }) {
  const label = statusLabel(status).toLowerCase()
  const cls =
    status === 0 ? 'badge-active' :
    status === 1 ? 'badge-challenged' :
    status === 2 ? 'badge-settled' :
    'badge-expired'
  return <span className={`badge ${cls}`}>{label}</span>
}

function verdictTone(v: StoredEvidence['verdict']): 'success' | 'danger' | 'warning' {
  if (v === 'pass' || v === 'supported') return 'success'
  if (v === 'fail' || v === 'contradicted') return 'danger'
  return 'warning'
}

function ConfidenceMeter({ value, tone }: { value: number; tone: 'success' | 'danger' | 'warning' }) {
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100)
  const barColor = tone === 'success' ? 'var(--success)' : tone === 'danger' ? 'var(--danger)' : 'var(--warning)'
  return (
    <div className="confidence-meter" role="meter" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
      <div className="confidence-bar" style={{ width: `${pct}%`, background: barColor }} />
      <span className="confidence-label" style={{ color: barColor }}>{pct}%</span>
    </div>
  )
}

function SpecPanel({ spec }: { spec: StoredSpec | null }) {
  const [open, setOpen] = useState(true)
  if (!spec) {
    return (
      <div className="card stack">
        <div className="eyebrow">AI Spec</div>
        <p className="text-dim" style={{ fontSize: '0.86rem' }}>
          The structured spec generated by the AI Architect is not available for this commitment.
          Only the onchain spec hash is stored on Monad.
        </p>
      </div>
    )
  }
  const s = spec.spec
  return (
    <div className="card stack">
      <button
        type="button"
        className="collapsible-head"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="eyebrow">AI Spec · Generated by Venice AI (Qwen / Llama 70B)</span>
        <span className="chevron" aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="stack-tight">
          {s.claim_text && (
            <div>
              <div className="eyebrow" style={{ marginBottom: '0.3rem' }}>Claim</div>
              <p style={{ fontSize: '0.95rem', lineHeight: 1.55 }}>{String(s.claim_text)}</p>
            </div>
          )}
          {s.verification_type && (
            <div className="detail-row">
              <span className="detail-label">Verification type</span>
              <span className="detail-value"><span className="badge badge-neutral">{String(s.verification_type)}</span></span>
            </div>
          )}
          {Array.isArray(s.success_criteria) && s.success_criteria.length > 0 && (
            <div>
              <div className="eyebrow" style={{ marginBottom: '0.3rem' }}>Success criteria</div>
              <ul className="spec-list">
                {s.success_criteria.map((c, i) => (
                  <li key={i}>{String(c)}</li>
                ))}
              </ul>
            </div>
          )}
          {Array.isArray(s.parties) && s.parties.length > 0 && (
            <div className="detail-row">
              <span className="detail-label">Parties</span>
              <span className="detail-value mono" style={{ fontSize: '0.78rem' }}>
                {s.parties.map((p) => shortAddr(String(p))).join(' · ')}
              </span>
            </div>
          )}
          {s.deadline_iso && (
            <div className="detail-row">
              <span className="detail-label">Deadline (ISO)</span>
              <span className="detail-value">{String(s.deadline_iso)}</span>
            </div>
          )}
          {s.stake_amount_mon !== undefined && (
            <div className="detail-row">
              <span className="detail-label">Stake</span>
              <span className="detail-value">{String(s.stake_amount_mon)} MON</span>
            </div>
          )}
          <div className="text-dim" style={{ fontSize: '0.72rem' }}>
            Spec stored: {new Date(spec.created_at).toLocaleString()}
          </div>
        </div>
      )}
    </div>
  )
}

function EvidencePanel({ evidence }: { evidence: StoredEvidence | null }) {
  if (!evidence) return null
  const tone = verdictTone(evidence.verdict)
  const toneCls = tone === 'success' ? 'alert-success' : tone === 'danger' ? 'alert-error' : 'alert-warning'
  const sources = Array.isArray(evidence.sources) ? evidence.sources : []
  return (
    <div className={`card stack ${toneCls}`}>
      <div className="row-between">
        <div>
          <div className="eyebrow">Evidence Verdict · Fact-checked by three.ws</div>
          <div className="verdict-value" style={{ marginTop: '0.3rem' }}>{evidence.verdict.toUpperCase()}</div>
        </div>
        <div style={{ minWidth: '180px' }}>
          <div className="eyebrow" style={{ marginBottom: '0.3rem' }}>Confidence</div>
          <ConfidenceMeter value={evidence.confidence} tone={tone} />
        </div>
      </div>

      {evidence.reasoning && (
        <p style={{ fontSize: '0.88rem', lineHeight: 1.6, opacity: 0.92 }}>{evidence.reasoning}</p>
      )}

      {sources.length > 0 && (
        <div>
          <div className="eyebrow" style={{ marginBottom: '0.4rem' }}>Sources cited</div>
          <ul className="sources-list">
            {sources.map((src, i) => {
              const url = typeof src === 'string' ? src : (src as any)?.url || (src as any)?.title || JSON.stringify(src)
              const isUrl = typeof url === 'string' && /^https?:\/\//.test(url)
              return (
                <li key={i}>
                  {isUrl ? (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="source-link">
                      {String(url).length > 70 ? String(url).slice(0, 67) + '…' : url}
                    </a>
                  ) : (
                    <span>{String(url)}</span>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {evidence.attestation && (
        <div className="stack-tight">
          <div className="eyebrow">SHA-256 Attestation</div>
          <div className="mono break-all" style={{ fontSize: '0.72rem', opacity: 0.85 }}>{evidence.attestation}</div>
        </div>
      )}

      <div className="text-dim" style={{ fontSize: '0.72rem' }}>
        Evidence stored: {new Date(evidence.stored_at).toLocaleString()}
      </div>
    </div>
  )
}

function Timeline({ commitment, hasEvidence }: { commitment: { status: number; deadline: bigint; evidenceHash: `0x${string}` }; hasEvidence: boolean }) {
  const steps = [
    { key: 'created', label: 'Created', done: true },
    { key: 'evidence', label: 'Evidence submitted', done: hasEvidence },
    { key: 'challenged', label: 'Challenged', done: commitment.status === 1 || commitment.status === 2 },
    { key: 'settled', label: 'Settled', done: commitment.status === 2 },
  ]
  const activeIdx = steps.findIndex((s) => !s.done)
  return (
    <div className="card stack">
      <div className="eyebrow">Lifecycle Timeline</div>
      <ol className="timeline">
        {steps.map((s, i) => {
          const isDone = s.done
          const isActive = i === activeIdx
          return (
            <li key={s.key} className={`timeline-step ${isDone ? 'done' : ''} ${isActive ? 'active' : ''}`}>
              <span className="timeline-dot" aria-hidden="true" />
              <span className="timeline-label">{s.label}</span>
              {isActive && <span className="timeline-now">now</span>}
            </li>
          )
        })}
      </ol>
    </div>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="detail-row">
      <dt className="detail-label">{label}</dt>
      <dd className="detail-value">{children}</dd>
    </div>
  )
}

export default function CommitmentDetail({ address, isConnected }: Props) {
  const { id } = useParams<{ id: string }>()
  const idBigInt = id && /^\d+$/.test(id) ? BigInt(id) : null

  const [challengeArg, setChallengeArg] = useState('')
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionTx, setActionTx] = useState<Hash | null>(null)

  const { writeContractAsync } = useWriteContract()
  const { isLoading: confirming, isSuccess: confirmed } = useWaitForTransactionReceipt({ hash: actionTx ?? undefined })

  const { data: commitment, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['commitment', idBigInt?.toString()],
    queryFn: () => fetchCommitment(idBigInt!),
    enabled: !!idBigInt,
    refetchInterval: 10_000,
  })

  const { data: inWindow } = useQuery({
    queryKey: ['challenge-window', idBigInt?.toString()],
    queryFn: () => fetchIsInChallengeWindow(idBigInt!),
    enabled: !!idBigInt,
    refetchInterval: 10_000,
  })

  // Fetch spec + evidence from KV in parallel (independent of commitment fetch)
  const { data: storedSpec } = useQuery<StoredSpec | null>({
    queryKey: ['spec', commitment?.specHash],
    queryFn: () => fetchSpec(commitment!.specHash),
    enabled: !!commitment?.specHash,
    retry: false,
  })

  const hasEvidenceHash = !!commitment?.evidenceHash && commitment.evidenceHash !== ZERO_BYTES32
  const { data: storedEvidence } = useQuery<StoredEvidence | null>({
    queryKey: ['evidence', commitment?.evidenceHash],
    queryFn: () => fetchEvidence(commitment!.evidenceHash),
    enabled: hasEvidenceHash,
    retry: false,
  })

  if (!idBigInt) {
    return (
      <div className="card alert alert-error">
        Invalid commitment ID. <Link to="/" className="text-primary">Back to feed</Link>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="card stack" aria-busy="true">
        <div className="skeleton" style={{ width: 96, height: 20 }} />
        <div className="skeleton" style={{ width: '100%', height: 14 }} />
        <div className="skeleton" style={{ width: '66%', height: 14 }} />
      </div>
    )
  }

  if (isError || !commitment) {
    return (
      <div className="card stack">
        <div className="alert alert-error">
          Couldn't load commitment #{id}: {error instanceof Error ? error.message.slice(0, 120) : 'not found'}
        </div>
        <button onClick={() => refetch()} className="btn btn-secondary btn-sm">Retry</button>
      </div>
    )
  }

  const lower = (a?: string) => a?.toLowerCase() ?? ''
  const isCreator = lower(address) === lower(commitment.creator)
  const isCounterparty = lower(address) === lower(commitment.counterparty)
  const isSelfCommitment = commitment.counterparty === '0x0000000000000000000000000000000000000000'
  const isActive = commitment.status === 0
  const isChallenged = commitment.status === 1
  const isSettled = commitment.status === 2
  const now = Math.floor(Date.now() / 1000)
  const deadlineSecs = Number(commitment.deadline) - now
  const windowOpen = !!inWindow
  const canAutoSettle = isActive && !windowOpen && deadlineSecs < 0

  const claim = storedSpec?.claim_text?.trim() || (typeof storedSpec?.spec?.claim_text === 'string' ? storedSpec.spec.claim_text : '')

  const handleAction = async (fnName: 'challenge' | 'settle', args: unknown[], label: string) => {
    setActionError(null)
    setActionTx(null)
    try {
      const hash = await writeContractAsync({
        address: VOUCH_CONTRACT_ADDRESS,
        abi: VOUCH_ABI,
        functionName: fnName,
        args,
      } as never)
      setActionTx(hash)
    } catch (err) {
      setActionError(`${label} failed: ${err instanceof Error ? err.message.slice(0, 140) : 'unknown error'}`)
    }
  }

  return (
    <div className="maxw-2xl stack">
      <Link to="/" className="detail-back">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M10 6H2m3 3-3-3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        Back to feed
      </Link>

      {/* 1. Claim Hero Card */}
      <div className="card stack hero-card">
        <div className="eyebrow mono">Commitment #{id}</div>
        {claim ? (
          <h1 className="hero-claim" title={claim}>“{claim}”</h1>
        ) : (
          <h1 className="hero-claim hero-claim-hash mono" title="Claim not yet stored — showing spec hash">
            {commitment.specHash.slice(0, 12)}…{commitment.specHash.slice(-6)}
          </h1>
        )}
        <div className="row-between" style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div className="hero-stake">
            <span className="hero-stake-value">{formatMon(commitment.stake)}</span>
            <span className="hero-stake-label">MON staked</span>
          </div>
          <StatusBadge status={commitment.status} />
        </div>
        <dl className="detail-list">
          <Row label="Creator">
            <span className="mono">{shortAddr(commitment.creator)}</span>
            {isCreator && <span className="badge badge-accent" style={{ marginLeft: '0.5rem' }}>you</span>}
          </Row>
          <Row label="Counterparty">
            {isSelfCommitment ? (
              <span className="text-muted">self-commitment</span>
            ) : (
              <>
                <span className="mono">{shortAddr(commitment.counterparty)}</span>
                {isCounterparty && <span className="badge badge-accent" style={{ marginLeft: '0.5rem' }}>you</span>}
              </>
            )}
          </Row>
          <Row label="Verification">
            <span className="badge badge-neutral">{vTypeLabel(commitment.vType)}</span>
          </Row>
          <Row label="Deadline">
            {deadlineSecs > 0 ? (
              <span className="text-primary">in {Math.floor(deadlineSecs / 3600)}h {Math.floor((deadlineSecs % 3600) / 60)}m</span>
            ) : (
              <span className="text-danger">passed</span>
            )}
            <div className="text-dim" style={{ fontSize: '0.72rem' }}>{new Date(Number(commitment.deadline) * 1000).toLocaleString()}</div>
          </Row>
          <Row label="Spec hash">
            <span className="mono break-all" style={{ fontSize: '0.72rem' }}>{commitment.specHash}</span>
          </Row>
          {hasEvidenceHash && (
            <Row label="Evidence hash">
              <span className="mono break-all" style={{ fontSize: '0.72rem' }}>{commitment.evidenceHash}</span>
            </Row>
          )}
        </dl>
      </div>

      {/* 2. AI Spec Panel */}
      <SpecPanel spec={storedSpec ?? null} />

      {/* 3. Evidence Panel */}
      {hasEvidenceHash && <EvidencePanel evidence={storedEvidence ?? null} />}

      {/* 4. Lifecycle Timeline */}
      <Timeline commitment={commitment} hasEvidence={hasEvidenceHash} />

      {/* Status-specific banner */}
      {isSettled && (
        <div className="alert alert-info">
          This commitment has been settled and the stake distributed.
        </div>
      )}

      {windowOpen && !isChallenged && !isSelfCommitment && (
        <div className="alert alert-warning">
          Challenge window open — counterparty may dispute within 24h of the deadline.
        </div>
      )}

      {isChallenged && (
        <div className="alert alert-warning">
          Challenged — awaiting AI adjudicator ruling.
        </div>
      )}

      {actionError && <div role="alert" className="alert alert-error">{actionError}</div>}
      {actionTx && (
        <div role="status" className="alert alert-success">
          {confirmed ? '✓ Confirmed' : confirming ? 'Confirming onchain…' : 'Submitted'}
          <div className="mono break-all" style={{ fontSize: '0.72rem', opacity: 0.8, marginTop: '0.3rem' }}>tx: {actionTx}</div>
        </div>
      )}

      {/* 6. Actions */}
      {isConnected && !isSettled && (
        <div className="card stack">
          <div className="section-label">Actions</div>

          {/* Counterparty: challenge during window */}
          {isCounterparty && isActive && windowOpen && (
            <div className="stack-tight">
              <label htmlFor="challenge-arg" className="label">Challenge argument (optional)</label>
              <textarea
                id="challenge-arg"
                className="textarea"
                placeholder="Why should this commitment be rejected?"
                value={challengeArg}
                onChange={(e) => setChallengeArg(e.target.value)}
                rows={2}
              />
              <button
                type="button"
                className="btn btn-danger btn-block"
                disabled={confirming}
                onClick={() => handleAction('challenge', [idBigInt], 'Challenge')}
              >
                {confirming ? 'Confirming…' : 'Challenge this commitment'}
              </button>
              {challengeArg && (
                <p className="text-dim" style={{ fontSize: '0.72rem' }}>
                  Note: the onchain challenge() takes no argument — your text here is for the AI adjudicator context and not stored onchain in this MVP.
                </p>
              )}
            </div>
          )}

          {/* Anyone: auto-settle after window closes if unchallenged */}
          {isActive && canAutoSettle && (
            <button
              type="button"
              className="btn btn-primary btn-block"
              disabled={confirming}
              onClick={() => handleAction('settle', [idBigInt, true], 'Settle')}
            >
              {confirming ? 'Confirming…' : 'Settle (creator wins — uncontested)'}
            </button>
          )}

          {/* Adjudicator-only: settle disputed */}
          {isChallenged && (
            <div className="stack-tight">
              <p className="text-muted" style={{ fontSize: '0.8rem' }}>
                Disputed commitments can only be settled by the onchain adjudicator wallet. The frontend will pass your ruling to <code className="code-inline">settle(id, creatorWins)</code>.
              </p>
              <div className="choice-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={confirming}
                  onClick={() => handleAction('settle', [idBigInt, true], 'Settle (creator wins)')}
                >
                  Creator wins
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={confirming}
                  onClick={() => handleAction('settle', [idBigInt, false], 'Settle (counterparty wins)')}
                >
                  Counterparty wins
                </button>
              </div>
            </div>
          )}

          {/* No actions available */}
          {!windowOpen && !canAutoSettle && !isChallenged && (
            <p className="text-muted" style={{ fontSize: '0.8rem' }}>
              No actions available for this commitment in its current state.
            </p>
          )}

          {!isConnected && (
            <p className="text-muted" style={{ fontSize: '0.8rem' }}>Connect your wallet to take action on this commitment.</p>
          )}
        </div>
      )}
    </div>
  )
}
